import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Check, Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";

// Map server plan keys → Stripe package_id. `indie_sandbox` is free → no checkout.
const CHECKOUT_PACKAGE = {
  regional: "regional",
  ad_film:  "ad_film",
  feature:  "feature",
  vendor:   "vendor_annual",
};

export default function Pricing() {
  const { user, featureFlags } = useAuth();
  const paymentsOff  = featureFlags && featureFlags.payments_enabled === false;
  const providerNoop = featureFlags && featureFlags.payment_provider === "noop";
  const nav = useNavigate();
  const [plans, setPlans] = useState([]);
  const [submittingKey, setSubmittingKey] = useState("");

  useEffect(() => {
    api.get("/meta/plans").then(({ data }) => setPlans(data));
  }, []);

  const HIGHLIGHTS = {
    indie_sandbox: ["20 crew hard cap", "Short film & student projects", "Free forever", "Digital iCard issued"],
    regional:      ["80 crew cap", "Regional cinema discounted plan", "Full procurement gate", "Geo-attendance"],
    ad_film:       ["120 crew cap", "Optimised for 3-week TVC shoots", "All procurement + attendance modules", "Audition vault + silent push"],
    feature:       ["250 crew cap", "Feature films + production houses", "Silent push call-sheet sync", "Headless audition vault"],
    vendor:        ["Annual catalog listing", "1 push token / month", "Promo shielded from labor tier", "Verified vendor badge"],
  };

  const choose = async (planKey) => {
    if (paymentsOff) {
      toast.success("Every feature is free during our beta — no plan needed.");
      return;
    }
    if (!user) {
      toast.error("Sign in to upgrade your plan");
      nav("/login");
      return;
    }
    if (planKey === "indie_sandbox") {
      toast.success("You're already on the free plan tier");
      return;
    }
    const pkg = CHECKOUT_PACKAGE[planKey];
    if (!pkg) { toast.error("This plan is not purchasable yet"); return; }
    setSubmittingKey(planKey);
    try {
      const { data } = await api.post("/payments/checkout/session", {
        package_id: pkg,
        origin_url: window.location.origin,
      });
      // Hard-nav to Stripe — same-tab is the documented pattern
      window.location.href = data.url;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Checkout failed");
      setSubmittingKey("");
    }
  };

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-12" data-testid="pricing-page">
      <div className="text-[10px] tracking-[0.3em] uppercase text-white/50">Subscription Engineering</div>
      <h1 className="font-display text-4xl sm:text-5xl uppercase tracking-tight mt-2">Plans & Caps</h1>
      <p className="mt-3 text-sm text-white/55 max-w-2xl">
        Pick the right shoot footprint. Crew caps are enforced by the backend — upgrade
        unlocks the next bracket instantly.
      </p>

      {paymentsOff && (
        <div
          className="mt-6 flex items-start gap-3 rounded-2xl border border-emerald-400/30 bg-emerald-500/10 px-5 py-4"
          data-testid="beta-payments-off-banner"
        >
          <Sparkles size={18} className="text-emerald-300 shrink-0 mt-0.5" />
          <div>
            <div className="text-sm font-semibold text-emerald-100">Free beta — all plans unlocked</div>
            <div className="mt-1 text-xs text-white/70">
              Payments are paused platform-wide while your team stress-tests the workflow.
              Every crew cap and paywall is bypassed. Existing subscribers keep their tier
              on record — no refunds, no downgrades. The plans below stay here for reference.
            </div>
          </div>
        </div>
      )}

      {!paymentsOff && providerNoop && (
        <div
          className="mt-6 flex items-start gap-3 rounded-2xl border border-amber-400/30 bg-amber-500/10 px-5 py-4"
          data-testid="payments-provider-noop-banner"
        >
          <Sparkles size={18} className="text-amber-300 shrink-0 mt-0.5" />
          <div>
            <div className="text-sm font-semibold text-amber-100">Payments coming soon</div>
            <div className="mt-1 text-xs text-white/70">
              We're finalising our payment-provider partner. The pricing sheet
              is live and every checkout intent is recorded — but real charges
              turn on the moment the provider deal is signed. Your KYC on
              <Link to="/payout-account" className="underline hover:text-amber-200 mx-1">payout account</Link>
              still moves ahead so payouts land on day one.
            </div>
          </div>
        </div>
      )}

      <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-4 gap-5">
        {plans.map((p) => {
          const isCurrent = user?.subscription_plan === p.key;
          const busy = submittingKey === p.key;
          return (
            <div key={p.key} className="cs-card rounded-2xl p-6 relative flex flex-col" data-testid={`plan-${p.key}`}>
              {p.key === "feature" && (
                <div className="absolute -top-3 left-6 text-[10px] tracking-[0.28em] uppercase px-2.5 py-1 rounded-full"
                     style={{ background: "var(--association-theme)", color: "#0a0a0a" }}>
                  Recommended
                </div>
              )}
              {isCurrent && (
                <div className="absolute -top-3 right-6 text-[10px] tracking-[0.28em] uppercase px-2.5 py-1 rounded-full bg-emerald-500/30 text-emerald-200 border border-emerald-400/30">
                  Current
                </div>
              )}
              <div className="text-[10px] tracking-[0.3em] uppercase text-white/50">{p.key.replace("_", " ")}</div>
              <div className="font-display text-2xl uppercase tracking-tight mt-1.5">{p.label}</div>
              <div className="mt-4 flex items-baseline gap-2">
                <span className="font-display text-4xl">{p.price_inr === 0 ? "FREE" : `₹${p.price_inr.toLocaleString("en-IN")}`}</span>
                {p.price_inr > 0 && <span className="text-xs text-white/50 uppercase tracking-wider">/ project</span>}
              </div>
              <ul className="mt-6 space-y-2.5 flex-1">
                {(HIGHLIGHTS[p.key] || []).map((h) => (
                  <li key={h} className="flex items-start gap-2 text-sm text-white/75">
                    <Check size={14} className="mt-0.5 shrink-0" style={{ color: "var(--association-theme)" }} />
                    <span>{h}</span>
                  </li>
                ))}
              </ul>
              <button
                onClick={() => choose(p.key)}
                disabled={busy || isCurrent || paymentsOff}
                className="mt-6 h-11 rounded-full text-xs uppercase tracking-[0.25em] font-semibold btn-themed inline-flex items-center justify-center disabled:opacity-50"
                data-testid={`plan-cta-${p.key}`}
              >
                {busy && <Loader2 size={14} className="mr-2 animate-spin" />}
                {paymentsOff
                  ? "Free in Beta"
                  : (isCurrent ? "Active Plan" : p.price_inr === 0 ? "Start Free" : "Choose Plan")}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
